import React, { useState, useMemo, useRef, useEffect } from "react";
import PicSelector from "@/Components/shared/PicSelector";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, ClipboardList, ChevronDown, ChevronUp, CheckCircle2,
  PlayCircle, Clock, AlertCircle, Pause, X, UserPlus, Trash2, RotateCcw, Pencil,
  Beaker, Droplet, Package, ShieldCheck, User, FileText
} from "lucide-react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { toast } from "sonner";
import { refundStockForTask } from "@/lib/stockDeduction";
import BatchActionBar from "@/components/ui/BatchActionBar";
import { Checkbox } from "@/components/ui/checkbox";

const ACTIVITY_OPTIONS = [
  { value: "racik", label: "Mixing", color: "bg-purple-100 text-purple-700", icon: Beaker },
  { value: "filling", label: "Filling", color: "bg-cyan-100 text-cyan-700", icon: Droplet },
  { value: "packing", label: "Pengemasan", color: "bg-orange-100 text-orange-700", icon: Package },
  { value: "other", label: "Lainnya", color: "bg-gray-100 text-gray-600", icon: ClipboardList },
];

const STATUS_CONFIG = {
  assigned: { label: "Belum Dimulai", cls: "bg-amber-100 text-amber-700", icon: AlertCircle },
  in_progress: { label: "Sedang Berjalan", cls: "bg-blue-100 text-blue-700", icon: PlayCircle },
  waiting_qc: { label: "Menunggu Validasi QC", cls: "bg-purple-100 text-purple-700 border border-purple-200 font-semibold", icon: ShieldCheck },
  menunggu_qc: { label: "Menunggu Validasi QC", cls: "bg-purple-100 text-purple-700 border border-purple-200 font-semibold", icon: ShieldCheck },
  completed: { label: "Selesai", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  on_hold: { label: "Ditunda", cls: "bg-gray-100 text-gray-600", icon: Pause },
  cancelled: { label: "Dibatalkan", cls: "bg-red-100 text-red-600", icon: X },
};

// Roles yang BOLEH dipilih (exclude developer & owner)
const ALLOWED_ROLES = ["admin", "kepala", "staff"];

const ROLE_LABELS = {
  admin: "Admin",
  kepala: "Kepala/Supervisor",
  staff: "Staff/Operator",
};

// Searchable member selector: search + dropdown satu kesatuan
function MemberRoleSelector({ label, roleValue, onRoleChange, memberValue, onMemberChange, members }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  // Semua member eligible, filter by role jika role dipilih
  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const matchRole = !roleValue || roleValue === "all" || m.role === roleValue;
      const matchSearch = !search || m.full_name?.toLowerCase().includes(search.toLowerCase());
      return matchRole && matchSearch;
    });
  }, [members, roleValue, search]);

  const selectedMember = members.find(m => m.id === memberValue);

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="w-full flex items-center justify-between h-9 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring text-left"
        >
          <span className={selectedMember ? "text-foreground" : "text-muted-foreground"}>
            {selectedMember
              ? `${selectedMember.full_name}${selectedMember.position ? ` · ${selectedMember.position}` : ""}`
              : "Cari dan pilih orang..."}
          </span>
          <svg className="w-4 h-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg">
            {/* Search + Role filter dalam satu panel */}
            <div className="p-2 border-b border-border space-y-1.5">
              <input
                autoFocus
                className="w-full h-7 px-2.5 text-xs rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Ketik untuk cari nama..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => onRoleChange("all")}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${(!roleValue || roleValue === "all") ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                >Semua</button>
                {ALLOWED_ROLES.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => onRoleChange(r)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${roleValue === r ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                  >{ROLE_LABELS[r]}</button>
                ))}
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {filteredMembers.length === 0 ? (
                <div className="px-3 py-3 text-xs text-muted-foreground text-center">Tidak ditemukan</div>
              ) : (
                filteredMembers.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      onMemberChange(m.id);
                      onRoleChange(m.role);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 flex items-center gap-2 transition-colors ${memberValue === m.id ? "bg-primary/10 text-primary font-semibold" : ""}`}
                  >
                    <span className="flex-1">{m.full_name}{m.position ? ` · ${m.position}` : ""}</span>
                    <span className="text-[10px] text-muted-foreground">{ROLE_LABELS[m.role] || m.role}</span>
                  </button>
                ))
              )}
            </div>
            {selectedMember && (
              <div className="p-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => { onMemberChange(""); setOpen(false); setSearch(""); }}
                  className="w-full text-xs text-muted-foreground hover:text-destructive text-center py-1"
                >Hapus pilihan</button>
              </div>
            )}
          </div>
        )}
        {/* Backdrop */}
        {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
      </div>
    </div>
  );
}

export default function TambahTugasSection({ order }) {
  const queryClient = useQueryClient();
  const productionId = order.id;
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);

  const emptyForm = {
    activity_type: "",
    variants: [],
    variant_details: {},
    pic_role: "",
    pic_id: "",
    pic_name: "",
    notes: "",
    task_date: new Date().toISOString().split("T")[0],
  };
  const [form, setForm] = useState(emptyForm);

  const productList = order.products || (order.product_name || order.variant_name ? [order] : []);
  const hasMultipleProducts = productList.length > 1;

  const { data: members = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.filter({ status: "aktif" }),
  });

  // Hanya anggota yang boleh dipilih (exclude developer & owner)
  const eligibleMembers = useMemo(() =>
    members.filter(m => ALLOWED_ROLES.includes(m.role)),
    [members]
  );

  const { data: allTasks = [] } = useQuery({
    queryKey: ["production-tasks", order.id],
    queryFn: () => base44.entities.ProductionTask.filter({ po_id: order.id }),
  });

  const { data: databaseBahanList = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list('nama_brand') });
  const { data: racikanDbList = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list('nama_brand') });

  // Auto-merge duplicate Curi Start tasks into the main task seamlessly
  const hasMergedRef = useRef(new Set());
  useEffect(() => {
    if (!allTasks || allTasks.length < 2) return;

    const packingTasks = allTasks.filter(t => ['packing', 'packaging', 'labeling', 'pengemasan'].includes(t.activity_type));
    if (packingTasks.length > 1) {
      // Find the early start task and the main task
      const earlyTask = packingTasks.find(t => 
        (t.notes && t.notes.includes('Curi Start')) ||
        t.status === 'waiting_qc' ||
        (t.task_name && t.task_name.includes('Wembley'))
      );
      const mainTask = packingTasks.find(t => t.id !== earlyTask?.id);

      if (earlyTask && mainTask && !hasMergedRef.current.has(earlyTask.id)) {
        hasMergedRef.current.add(earlyTask.id);
        (async () => {
          try {
            const mergedMemberProgress = {
              ...(mainTask.member_progress || {}),
              ...(earlyTask.member_progress || {})
            };
            const mergedGrossMins = Math.max(Number(mainTask.gross_minutes || 0), Number(earlyTask.gross_minutes || 0));
            const mergedDurMins = Math.max(Number(mainTask.duration_minutes || 0), Number(earlyTask.duration_minutes || 0));
            const mergedStartTime = earlyTask.start_time || mainTask.start_time;
            const mergedEndTime = earlyTask.end_time || mainTask.end_time;
            const mergedStatus = ['waiting_qc', 'completed'].includes(earlyTask.status) ? earlyTask.status : (mainTask.status || earlyTask.status);
            const notesArr = [mainTask.notes, earlyTask.notes].filter(Boolean);
            const mergedNotes = Array.from(new Set(notesArr)).join(' | ');

            await base44.entities.ProductionTask.update(mainTask.id, {
              status: mergedStatus,
              pic_id: earlyTask.pic_id || mainTask.pic_id,
              pic_name: earlyTask.pic_name || mainTask.pic_name,
              start_time: mergedStartTime,
              end_time: mergedEndTime,
              gross_minutes: mergedGrossMins > 0 ? mergedGrossMins : null,
              duration_minutes: mergedDurMins > 0 ? mergedDurMins : null,
              member_progress: mergedMemberProgress,
              notes: mergedNotes,
            });

            await base44.entities.ProductionTask.delete(earlyTask.id);
            queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
            queryClient.invalidateQueries({ queryKey: ["production-tasks", order.id] });
          } catch (err) {
            console.warn("Failed to auto-merge early start task:", err);
          }
        })();
      }
    }
  }, [allTasks, order.id, queryClient]);

  // Display deduplicated tasks in UI to ensure instant unified presentation
  const displayTasks = useMemo(() => {
    const map = new Map();
    (allTasks || []).forEach(t => {
      const actKey = ['packing', 'packaging', 'labeling', 'pengemasan'].includes(t.activity_type) ? 'packing' : t.activity_type;
      const key = `${t.po_id || order.id}_${actKey}`;
      if (!map.has(key)) {
        map.set(key, { ...t });
      } else {
        const existing = map.get(key);
        const mergedProg = { ...(existing.member_progress || {}), ...(t.member_progress || {}) };
        const mergedDur = Math.max(Number(existing.duration_minutes || 0), Number(t.duration_minutes || 0));
        const preferredStatus = ['waiting_qc', 'completed'].includes(t.status) ? t.status : existing.status;
        const preferredNotes = Array.from(new Set([existing.notes, t.notes].filter(Boolean))).join(' | ');
        map.set(key, {
          ...existing,
          status: preferredStatus,
          duration_minutes: mergedDur,
          member_progress: mergedProg,
          notes: preferredNotes,
          pic_name: t.pic_name || existing.pic_name,
          start_time: existing.start_time || t.start_time,
        });
      }
    });
    return Array.from(map.values());
  }, [allTasks, order.id]);

  const actTasks = useMemo(() => displayTasks.filter(t => ["assigned", "in_progress", "on_hold", "waiting_qc"].includes(t.status)), [displayTasks]);
  const doneTasks = useMemo(() => displayTasks.filter(t => ["completed", "cancelled"].includes(t.status)), [displayTasks]);

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.ProductionTask.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-tasks", order.id] });
      queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["production-order", order.id] });
      toast.success("Tugas berhasil ditambahkan!");
      setShowForm(false);
      setForm(emptyForm);
    },
  });

  const getProductDisplayName = (p) => {
    let name = (p.product_name || p.variant_name || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
    const size = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml;
    if (size) name += ` (${size}ml)`;
    return name;
  };

  const getVariantFormulaInfo = (vName, p, remainingPcs) => {
    const sizeMl = Number(p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 30);
    const totalMl = (remainingPcs || 0) * sizeMl;
    const totalLiter = totalMl > 0 ? parseFloat((totalMl / 1000).toFixed(2)) : 0;
    
    let rawRacikan = p.racikan_digunakan || p.racikan || [];
    if (!rawRacikan.length) {
      const vClean = (p.product_name || p.variant_name || vName || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim().toLowerCase();
      const bBrand = (order?.brand_name || '').toLowerCase().trim();
      const matchedBahan = databaseBahanList.find(b => (b.nama_varian || '').toLowerCase().trim() === vClean && (!b.nama_brand || bBrand.includes(b.nama_brand.toLowerCase().trim())));
      if (matchedBahan?.racikan?.length) rawRacikan = matchedBahan.racikan;
      else {
        const matchedDb = racikanDbList.find(db => (db.nama_varian || '').toLowerCase().trim() === vClean && (!db.nama_brand || bBrand.includes(db.nama_brand.toLowerCase().trim())));
        if (matchedDb?.racikan?.length) rawRacikan = matchedDb.racikan;
        else if (order?.products?.length === 1 && order.racikan_digunakan?.length) {
          rawRacikan = order.racikan_digunakan;
        }
      }
    }

    const ingredients = rawRacikan.map(r => {
      const pFloat = parseFloat(String(r.persentase || '0').replace(',', '.')) || 0;
      const volMl = (totalMl * pFloat) / 100;
      const volL = volMl / 1000;
      const nama = r.nama_bahan || r.bahan_nama || r.nama || r.kategori_bahan || 'Bahan';
      const kategori = r.kategori_bahan || r.kategori || 'Bibit';
      const formatted = volMl >= 1000 
        ? `${parseFloat(volL.toFixed(2))}L` 
        : `${Math.round(volMl * 10) / 10}ml`;
      return {
        nama,
        kategori,
        persentase: pFloat,
        vol_ml: volMl,
        vol_liter: volL,
        formatted,
        vendor: r.vendor || '',
      };
    });

    return {
      sizeMl,
      totalMl,
      totalLiter,
      ingredients,
    };
  };

  const getVariantProgressInfo = (vName, p) => {
    let totalRejects = 0;
    let completedCurrentStage = 0;
    
    allTasks.forEach(task => {
      // Hanya hitung task untuk varian ini
      if (task.product_name === vName || (task.variants && task.variants.includes(vName))) {
        // Hitung rejects
        if (task.member_progress) {
          Object.values(task.member_progress).forEach(prog => {
            if (prog.status === 'completed' && prog.rejected_items) {
              prog.rejected_items.forEach(rj => {
                rj.items.forEach(item => {
                  if (item.qty > 0) totalRejects += Number(item.qty);
                });
              });
            }
          });
        }
        
        // Hitung completed qty untuk stage yang sedang dipilih
        if (task.activity_type === form.activity_type) {
          if (task.member_progress) {
            Object.values(task.member_progress).forEach(prog => {
              if (prog.status === 'completed' && prog.items) {
                prog.items.forEach(i => completedCurrentStage += Number(i.qty));
              }
            });
          } else if (['completed'].includes(task.status)) {
            completedCurrentStage += Number(task.final_quantity) || Number(task.target_quantity) || 0;
          }
        }
      }
    });

    const targetQty = Number(p.quantity) || Number(p.target_qty) || 0;
    const validTarget = Math.max(0, targetQty - totalRejects);
    const remaining = Math.max(0, validTarget - completedCurrentStage);
    const formulaInfo = getVariantFormulaInfo(vName, p, remaining);
    
    return {
      target: validTarget,
      completed: completedCurrentStage,
      remaining,
      rejects: totalRejects,
      formula: formulaInfo,
    };
  };

  // Helper untuk generate catatan mixing otomatis
  const buildAutoNotes = (activityType, variants, variantDetails) => {
    if (activityType !== "racik" || !variants || variants.length === 0) return "";
    let lines = ["[Rincian Racikan Per Varian]:"];
    let totalVol = 0;
    let mainUnit = "liter";

    variants.forEach(vName => {
      const det = variantDetails[vName];
      if (!det) return;
      const vQty = det.qty ?? 0;
      const vUnit = det.unit || "liter";
      mainUnit = vUnit;
      totalVol += Number(vQty);

      let line = `• ${vName}: ${vQty} ${vUnit}`;
      if (det.pcs_target && det.botol_size_ml) {
        line += ` (${det.pcs_target} botol @${det.botol_size_ml}ml)`;
      }
      lines.push(line);
    });

    lines.push(`Total Volume Racikan: ${parseFloat(totalVol.toFixed(2))} ${mainUnit}`);
    return lines.join("\n");
  };

  const handleActivityChange = (val) => {
    const isMixing = val === "racik";
    setForm((f) => {
      const newDetails = {};
      (f.variants || []).forEach(vName => {
        const p = productList.find(pr => getProductDisplayName(pr) === vName) || {};
        const info = getVariantProgressInfo(vName, p);
        if (isMixing) {
          newDetails[vName] = {
            qty: info.formula.totalLiter || (info.formula.totalMl > 0 ? parseFloat((info.formula.totalMl / 1000).toFixed(2)) : info.remaining),
            unit: "liter",
            pcs_target: info.remaining,
            botol_size_ml: info.formula.sizeMl,
            total_ml: info.formula.totalMl,
            racikan_breakdown: info.formula.ingredients,
          };
        } else {
          newDetails[vName] = {
            qty: info.remaining,
            unit: "pcs",
          };
        }
      });

      const autoNote = isMixing ? buildAutoNotes(val, f.variants, newDetails) : "";

      return {
        ...f,
        activity_type: val,
        variant_details: newDetails,
        notes: isMixing && (!f.notes || f.notes.startsWith("[Rincian Racikan")) ? autoNote : f.notes,
      };
    });
  };

  const handleToggleVariant = (vName, p) => {
    if (!form.activity_type) {
      toast.error("Pilih jenis tugas dulu agar bisa menghitung sisa Qty");
      return;
    }
    const isMixing = form.activity_type === "racik";
    setForm(f => {
      const isSelected = (f.variants || []).includes(vName);
      let newVariants;
      let newDetails = { ...f.variant_details };
      if (isSelected) {
        newVariants = (f.variants || []).filter(v => v !== vName);
        delete newDetails[vName];
      } else {
        newVariants = [...(f.variants || []), vName];
        const info = getVariantProgressInfo(vName, p);
        if (isMixing) {
          newDetails[vName] = {
            qty: info.formula.totalLiter || (info.formula.totalMl > 0 ? parseFloat((info.formula.totalMl / 1000).toFixed(2)) : info.remaining),
            unit: "liter",
            pcs_target: info.remaining,
            botol_size_ml: info.formula.sizeMl,
            total_ml: info.formula.totalMl,
            racikan_breakdown: info.formula.ingredients,
          };
        } else {
          newDetails[vName] = {
            qty: info.remaining,
            unit: "pcs",
          };
        }
      }

      const autoNote = isMixing ? buildAutoNotes(form.activity_type, newVariants, newDetails) : f.notes;

      return { 
        ...f, 
        variants: newVariants, 
        variant_details: newDetails,
        notes: isMixing && (!f.notes || f.notes.startsWith("[Rincian Racikan")) ? autoNote : f.notes,
      };
    });
  };

  const handleToggleAllVariants = () => {
    if (!form.activity_type) {
      toast.error("Pilih jenis tugas dulu agar bisa menghitung sisa Qty");
      return;
    }
    const isMixing = form.activity_type === "racik";
    setForm(f => {
      if ((f.variants || []).length === productList.length) {
        return { 
          ...f, 
          variants: [], 
          variant_details: {},
          notes: isMixing && f.notes.startsWith("[Rincian Racikan") ? "" : f.notes
        };
      }
      const newVariants = productList.map(p => getProductDisplayName(p));
      const newDetails = {};
      productList.forEach(p => {
        const vName = getProductDisplayName(p);
        const info = getVariantProgressInfo(vName, p);
        if (isMixing) {
          newDetails[vName] = {
            qty: info.formula.totalLiter || (info.formula.totalMl > 0 ? parseFloat((info.formula.totalMl / 1000).toFixed(2)) : info.remaining),
            unit: "liter",
            pcs_target: info.remaining,
            botol_size_ml: info.formula.sizeMl,
            total_ml: info.formula.totalMl,
            racikan_breakdown: info.formula.ingredients,
          };
        } else {
          newDetails[vName] = {
            qty: info.remaining,
            unit: "pcs",
          };
        }
      });

      const autoNote = isMixing ? buildAutoNotes(form.activity_type, newVariants, newDetails) : f.notes;

      return { 
        ...f, 
        variants: newVariants, 
        variant_details: newDetails,
        notes: isMixing && (!f.notes || f.notes.startsWith("[Rincian Racikan")) ? autoNote : f.notes,
      };
    });
  };

  const handleSubmit = async () => {
    if (!form.activity_type) return toast.error("Pilih jenis tugas dulu");
    if (!form.variants || form.variants.length === 0) return toast.error("Pilih minimal satu variant dulu");

    const activityOpt = ACTIVITY_OPTIONS.find((o) => o.value === form.activity_type);
    const activityLabel = activityOpt ? activityOpt.label.replace(/^.*?\s/, "") : form.activity_type;
    const isMixing = form.activity_type === "racik";
    
    const brandPrefix = (order.brand_name || "Tanpa Brand").split('/')[0].trim();
    
    try {
      // Hitung akumulasi quantity dan unit utama
      const totalQty = form.variants.reduce((sum, v) => sum + (Number(form.variant_details[v]?.qty) || 0), 0);
      const primaryUnit = form.variant_details[form.variants[0]]?.unit || (isMixing ? "liter" : "pcs");

      // Buat 1 tugas per varian yang dipilih agar bisa dikontrol secara independen (bisa mulai bersamaan atau satu per satu)
      if (form.variants.length > 1) {
        const promises = form.variants.map((vName) => {
          const p = productList.find(pr => getProductDisplayName(pr) === vName) || {};
          const det = form.variant_details[vName] || {};
          const vQty = Number(det.qty) || 0;
          const vUnit = det.unit || (isMixing ? "liter" : "pcs");
          const botolSize = det.botol_size_ml || p?.botol_ukuran_aktual || p?.botol_ukuran_label || p?.botol_ukuran_client || p?.ukuran_botol_ml || (isMixing ? 30 : null);
          const racikNotes = form.notes || null;

          return base44.entities.ProductionTask.create({
            task_name: `${brandPrefix} - ${vName} - ${activityLabel}`,
            po_id: order.id,
            po_number: String(order.order_number),
            po_brand: order.brand_name,
            activity_type: form.activity_type,
            product_name: vName,
            botol_ukuran_aktual: botolSize,
            target_quantity: vQty,
            unit: vUnit,
            pic_id: form.pic_id || null,
            pic_name: form.pic_name || null,
            pic_role: form.pic_role || null,
            notes: racikNotes,
            task_date: form.task_date,
            status: "assigned",
          });
        });

        await Promise.all(promises);
      } else {
        const vName = form.variants[0];
        const p = productList.find(pr => getProductDisplayName(pr) === vName) || {};
        const det = form.variant_details[vName] || {};
        const vQty = Number(det.qty) || totalQty;
        const vUnit = det.unit || primaryUnit;
        const botolSize = det.botol_size_ml || p?.botol_ukuran_aktual || p?.botol_ukuran_label || p?.botol_ukuran_client || p?.ukuran_botol_ml || (isMixing ? 30 : null);

        const taskData = {
          task_name: `${brandPrefix} - ${vName} - ${activityLabel}`,
          po_id: order.id,
          po_number: String(order.order_number),
          po_brand: order.brand_name,
          activity_type: form.activity_type,
          product_name: vName,
          botol_ukuran_aktual: botolSize,
          target_quantity: vQty,
          unit: vUnit,
          pic_id: form.pic_id || null,
          pic_name: form.pic_name || null,
          pic_role: form.pic_role || null,
          notes: form.notes,
          task_date: form.task_date,
          status: "assigned",
        };
        
        await base44.entities.ProductionTask.create(taskData);
      }
      
      queryClient.invalidateQueries({ queryKey: ["production-tasks", order.id] });
      queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["production-order", order.id] });
      toast.success(`Berhasil menambahkan ${form.variants.length} tugas produksi (${isMixing ? `${parseFloat(totalQty.toFixed(2))} ${primaryUnit}` : `${totalQty} ${primaryUnit}`})!`);
      
      setShowForm(false);
      setForm(emptyForm);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Gagal menambahkan tugas");
    }
  };

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => base44.entities.ProductionTask.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries(["production-tasks", order.id]);
      queryClient.invalidateQueries(["production-tasks"]);
      toast.success("Tugas berhasil dihapus");
    },
    onError: (err) => {
      toast.error(err.message || "Gagal menghapus tugas");
    }
  });

  const handleDeleteTask = (taskId) => {
    if (confirm("Apakah Anda yakin ingin menghapus tugas ini?")) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const deleteBatchMutation = useMutation({
    mutationFn: async (taskIds) => {
      const promises = taskIds.map(id => base44.entities.ProductionTask.delete(id));
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["production-tasks", order.id]);
      queryClient.invalidateQueries(["production-tasks"]);
      toast.success("Tugas terpilih berhasil dihapus");
      setSelectedTasks([]);
    },
    onError: (err) => {
      toast.error(err.message || "Gagal menghapus tugas");
    }
  });

  const handleBatchDelete = () => {
    deleteBatchMutation.mutate(selectedTasks);
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === allTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(allTasks.map(t => t.id));
    }
  };

  const handleToggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const editTaskMutation = useMutation({
    mutationFn: async ({ taskId, data, isUndo }) => {
      // 1. Refund stok jika ini adalah Undo dari status "Selesai"
      if (isUndo) {
        try {
          const [inventoryItems, bahanList] = await Promise.all([
            base44.entities.InventoryItem.filter({}),
            base44.entities.MasterBahanList.filter({})
          ]);
          await refundStockForTask(order, data.activity_type || data.activity, inventoryItems, bahanList);
        } catch (e) {
          console.warn('Gagal refund stok:', e);
        }
      }
      
      // 2. Update status tugas
      return base44.entities.ProductionTask.update(taskId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["production-tasks", order.id]);
      queryClient.invalidateQueries(["production-tasks"]);
      toast.success("Tugas berhasil diperbarui");
      setEditingTask(null);
    },
    onError: (err) => {
      toast.error(err.message || "Gagal memperbarui tugas");
    }
  });

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    setEditForm({
      status: task.status || "assigned",
      activity_type: task.activity_type || "racik",
      target_quantity: task.target_quantity !== undefined && task.target_quantity !== null ? String(task.target_quantity) : "",
      unit: task.unit || (task.activity_type === "racik" ? "liter" : "pcs"),
      pic_id: task.pic_id || "",
      pic_name: task.pic_name || "",
      pic_role: task.pic_role || "",
      notes: (task.notes || "").replace(/\[Selesai Dikerjakan\]\s*Menunggu Validasi QC/gi, '').trim(),
    });
  };

  const handleSaveEdit = () => {
    if (!editForm) return;
    
    // Apakah statusnya berubah dari completed ke bukan completed?
    const isUndo = editingTask.status === "completed" && editForm.status !== "completed";
    
    const parsedTarget = editForm.target_quantity !== "" ? Number(editForm.target_quantity) : editingTask.target_quantity;
    
    editTaskMutation.mutate({
      taskId: editingTask.id,
      data: {
        status: editForm.status,
        activity_type: editForm.activity_type,
        target_quantity: parsedTarget,
        unit: editForm.unit || editingTask.unit || (editForm.activity_type === "racik" ? "liter" : "pcs"),
        pic_id: editForm.pic_id,
        pic_name: editForm.pic_name,
        pic_role: editForm.pic_role,
        notes: editForm.notes,
      },
      isUndo,
    });
  };

  return (
    <>
      <BatchActionBar
        isSelectionMode={selectedTasks.length > 0}
        selectedCount={selectedTasks.length}
        totalCount={allTasks.length}
        onSelectAll={handleSelectAll}
        onClearSelection={() => setSelectedTasks([])}
        onBatchDelete={handleBatchDelete}
        isDeleting={deleteBatchMutation.isPending}
      />
      <Card>
        <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Tugas Produksi
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {displayTasks.length} tugas · {actTasks.length} aktif · {doneTasks.length} selesai
            </p>
          </div>
          <Button size="sm" className="gap-1.5 text-xs h-8" onClick={() => setShowForm((v) => !v)}>
            <Plus className="w-3 h-3" />
            Tambah Tugas
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Form Tambah */}
        {showForm && (
          <div className="border border-primary/20 bg-primary/5 rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-primary">Tugas Baru</p>

            <div>
              <Label className="text-xs mb-1 block">Jenis Tugas *</Label>
              <Select value={form.activity_type} onValueChange={handleActivityChange}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Pilih jenis tugas..." />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        {opt.icon && <opt.icon className="w-4 h-4" />} {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs mb-1 block">Varian (Pilih Variant) *</Label>
              <div className="flex flex-wrap gap-2">
                {productList.map(p => {
                  const vName = getProductDisplayName(p);
                  const isSelected = (form.variants || []).includes(vName);
                  return (
                    <Badge
                      key={vName}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer gap-1"
                      onClick={() => handleToggleVariant(vName, p)}
                    >
                      {vName}
                      {isSelected && <X className="w-3 h-3" />}
                    </Badge>
                  );
                })}
                {hasMultipleProducts && (
                  <Badge
                    variant={(form.variants || []).length === productList.length ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={handleToggleAllVariants}
                  >
                    {(form.variants || []).length === productList.length ? "Batal Pilih Semua" : "Pilih Semua Variant"}
                  </Badge>
                )}
              </div>
              
              {(form.variants || []).length > 0 && (
                <div className="bg-muted/30 rounded-lg border p-3 mt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] font-semibold block text-muted-foreground uppercase tracking-wider">
                      Detail Variant {form.activity_type === "racik" ? "(Kebutuhan Volume & Racikan)" : "(Target Output)"}
                    </Label>
                    {form.activity_type === "racik" && (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">
                        ✨ Auto-Kalkulasi Formula
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground font-medium pb-1 border-b">
                      <div className="col-span-6">Variant & Ukuran Botol</div>
                      <div className="col-span-3">{form.activity_type === "racik" ? "Volume *" : "Qty *"}</div>
                      <div className="col-span-3">Satuan *</div>
                    </div>
                    {(form.variants || []).map(vName => {
                      const p = productList.find(pr => getProductDisplayName(pr) === vName) || {};
                      const info = getVariantProgressInfo(vName, p);
                      const isMixing = form.activity_type === "racik";
                      const currentDetail = form.variant_details[vName] || {};
                      const ingredients = currentDetail.racikan_breakdown || info.formula?.ingredients || [];

                      return (
                      <div key={vName} className="p-2 rounded-lg bg-background/60 border border-border/50 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6 flex flex-col justify-center">
                            <span className="text-xs font-semibold truncate" title={vName}>{vName}</span>
                            <span className="text-[10px] text-muted-foreground mt-0.5">
                              {isMixing ? (
                                <span>
                                  Sisa: <strong>{info.remaining} pcs</strong> @{info.formula?.sizeMl || 30}ml = <strong>{info.formula?.totalLiter} L</strong>
                                </span>
                              ) : (
                                info.completed > 0 ? `${info.completed} selesai, kurang ${info.remaining}` : `Target: ${info.remaining} pcs`
                              )}
                            </span>
                          </div>
                          <div className="col-span-3">
                            <Input 
                              type="number" 
                              step="any"
                              value={currentDetail.qty ?? ""} 
                              onChange={(e) => {
                                const valStr = e.target.value;
                                const val = valStr === "" ? "" : Number(valStr);
                                
                                setForm(f => {
                                  const updatedDetails = {
                                    ...f.variant_details,
                                    [vName]: { 
                                      ...f.variant_details[vName], 
                                      qty: val,
                                      // Jika diedit manual pada mixing, hitung ulang proporsi takaran bahan
                                      ...(isMixing && val !== "" ? {
                                        total_ml: (currentDetail.unit || "liter") === "liter" ? val * 1000 : val,
                                        racikan_breakdown: (currentDetail.racikan_breakdown || []).map(b => {
                                          const baseMl = (currentDetail.unit || "liter") === "liter" ? val * 1000 : val;
                                          const vMl = (baseMl * b.persentase) / 100;
                                          const vL = vMl / 1000;
                                          return {
                                            ...b,
                                            vol_ml: vMl,
                                            vol_liter: vL,
                                            formatted: vMl >= 1000 ? `${parseFloat(vL.toFixed(2))}L` : `${Math.round(vMl * 10) / 10}ml`
                                          };
                                        })
                                      } : {})
                                    }
                                  };
                                  const autoNote = isMixing ? buildAutoNotes(f.activity_type, f.variants, updatedDetails) : f.notes;
                                  return {
                                    ...f,
                                    variant_details: updatedDetails,
                                    notes: isMixing && (!f.notes || f.notes.startsWith("[Rincian Racikan")) ? autoNote : f.notes,
                                  };
                                });
                              }}
                              className="h-7 text-xs font-semibold" 
                            />
                          </div>
                          <div className="col-span-3">
                            <Select 
                              value={currentDetail.unit || (isMixing ? "liter" : "pcs")} 
                              onValueChange={(val) => setForm(f => {
                                const updatedDetails = {
                                  ...f.variant_details,
                                  [vName]: { ...f.variant_details[vName], unit: val }
                                };
                                const autoNote = isMixing ? buildAutoNotes(f.activity_type, f.variants, updatedDetails) : f.notes;
                                return {
                                  ...f,
                                  variant_details: updatedDetails,
                                  notes: isMixing && (!f.notes || f.notes.startsWith("[Rincian Racikan")) ? autoNote : f.notes,
                                };
                              })}
                            >
                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {isMixing ? (
                                  <>
                                    <SelectItem value="liter" className="text-xs">liter</SelectItem>
                                    <SelectItem value="ml" className="text-xs">ml</SelectItem>
                                    <SelectItem value="kg" className="text-xs">kg</SelectItem>
                                    <SelectItem value="gram" className="text-xs">gram</SelectItem>
                                  </>
                                ) : (
                                  ["pcs", "botol", "kg", "liter", "ml", "gram", "lusin"].map((u) => (
                                    <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Rincian Komposisi Racikan Per Bahan (Khusus Mixing) */}
                        {isMixing && ingredients.length > 0 && (
                          <div className="pt-1.5 border-t border-border/40 text-[11px] bg-purple-50/40 rounded p-1.5 space-y-1">
                            <span className="font-semibold text-purple-900 block text-[10px]">
                              🧪 Komposisi Racikan ({vName}):
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {ingredients.map((ing, idx) => (
                                <Badge key={idx} variant="outline" className="text-[10px] bg-white border-purple-200 text-purple-800 py-0">
                                  {ing.nama}: <strong>{ing.formatted}</strong> ({ing.persentase}%)
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )})}
                  </div>

                  {/* Summary Akumulasi Total Multi-Variant */}
                  <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-bold text-primary">
                        Total Target {form.activity_type === "racik" ? "Volume Mixing" : "Output"}:
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {form.variants.length} Varian dipilih
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-base font-bold text-primary">
                        {parseFloat(
                          form.variants.reduce((sum, v) => sum + (Number(form.variant_details[v]?.qty) || 0), 0).toFixed(2)
                        )}{" "}
                        {form.activity_type === "racik" ? (form.variant_details[form.variants[0]]?.unit || "liter") : (form.variant_details[form.variants[0]]?.unit || "pcs")}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <PicSelector
              label="Penanggung Jawab (PIC Tugas Operator)"
              placeholder="Pilih operator yang mengerjakan..."
              value={form.pic_name}
              onChange={(name, member) => {
                setForm(f => ({ 
                  ...f, 
                  pic_name: name,
                  pic_id: member?.id || "", 
                  pic_role: member?.role || "" 
                }));
              }}
              picType="task"
              taskType={form.activity_type}
            />

            <div>
              <Label className="text-xs mb-1 block">Tanggal Tugas</Label>
              <Input
                type="date"
                value={form.task_date}
                onChange={(e) => setForm((f) => ({ ...f, task_date: e.target.value }))}
                className="h-9"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Catatan & Instruksi</Label>
                {form.activity_type === "racik" && form.variants?.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const autoNote = buildAutoNotes(form.activity_type, form.variants, form.variant_details);
                      setForm(f => ({ ...f, notes: autoNote }));
                      toast.info("Catatan rincian racikan berhasil digenerate!");
                    }}
                    className="text-[11px] text-purple-700 hover:text-purple-900 font-medium hover:underline flex items-center gap-1"
                  >
                    🔄 Isi Rincian Racikan Otomatis
                  </button>
                )}
              </div>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Instruksi atau catatan khusus..."
                rows={form.activity_type === "racik" && form.notes ? 4 : 2}
                className="resize-y text-xs font-mono"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                Batal
              </Button>
              <Button size="sm" className="flex-1" onClick={handleSubmit} disabled={createMutation.isPending}>
                {createMutation.isPending ? "Menyimpan..." : "Tambah Tugas"}
              </Button>
            </div>
          </div>
        )}

        {/* Task List */}
        {displayTasks.length === 0 && !showForm ? (
          <div className="text-center py-8 text-muted-foreground">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-20" />
            <p className="text-sm">Belum ada tugas. Klik "Tambah Tugas" untuk mulai.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayTasks.map((task) => {
              const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.assigned;
              const StatusIcon = statusCfg.icon;
              const actOpt = ACTIVITY_OPTIONS.find((o) => o.value === task.activity_type);
              const isExpanded = expanded === task.id;
              const sub = task.sub_stages || {};

              const stageMixingTask = displayTasks.find(t => t.activity_type === 'racik' || t.activity_type === 'mixing');
              const stageFillingTask = displayTasks.find(t => t.activity_type === 'filling');
              const stagePackingTask = displayTasks.find(t => ['packing', 'packaging', 'labeling', 'pengemasan'].includes(t.activity_type));

              const mixPic = sub.mixing?.pic_name || (task.activity_type === 'racik' ? task.pic_name : stageMixingTask?.pic_name);
              const mixStatus = sub.mixing?.status || (task.activity_type === 'racik' ? task.status : stageMixingTask?.status);

              const fillPic = sub.filling?.pic_name || (task.activity_type === 'filling' ? task.pic_name : stageFillingTask?.pic_name);
              const fillStatus = sub.filling?.status || (task.activity_type === 'filling' ? task.status : stageFillingTask?.status);

              const packPic = sub.packing?.pic_name || (['packing', 'packaging', 'labeling', 'pengemasan'].includes(task.activity_type) ? task.pic_name : stagePackingTask?.pic_name);
              const packStatus = sub.packing?.status || (['packing', 'packaging', 'labeling', 'pengemasan'].includes(task.activity_type) ? task.status : stagePackingTask?.status);

              const getStageDetails = (stageObj, stageTask) => {
                const durMins = stageObj?.duration_minutes || stageTask?.duration_minutes || null;
                const start = stageObj?.start_time || stageTask?.start_time || null;
                const end = stageObj?.completed_at || stageObj?.end_time || stageTask?.completed_at || stageTask?.end_time || null;
                const st = stageObj?.status || stageTask?.status || 'assigned';

                let mins = durMins;
                if (!mins && start && end) {
                  const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
                  if (diff > 0) mins = diff;
                }

                let formatted = null;
                if (mins && mins > 0) {
                  if (mins >= 60) {
                    const h = Math.floor(mins / 60);
                    const m = mins % 60;
                    formatted = `${h}j ${m > 0 ? `${m}m` : ''}`.trim();
                  } else {
                    formatted = `${mins}m`;
                  }
                }

                let range = null;
                if (start) {
                  try {
                    const startStr = format(new Date(start), "HH:mm");
                    if (end) {
                      const endStr = format(new Date(end), "HH:mm");
                      range = `${startStr} - ${endStr}`;
                    } else if (st === 'in_progress') {
                      range = `Mulai ${startStr}`;
                    }
                  } catch (e) {}
                }

                return { mins: mins || 0, formatted, range, status: st };
              };

              const mixInfo = getStageDetails(sub.mixing, task.activity_type === 'racik' ? task : stageMixingTask);
              const fillInfo = getStageDetails(sub.filling, task.activity_type === 'filling' ? task : stageFillingTask);
              const packInfo = getStageDetails(sub.packing, ['packing', 'packaging', 'labeling'].includes(task.activity_type) ? task : stagePackingTask);

              const totalMins = task.duration_minutes || (mixInfo.mins + fillInfo.mins + packInfo.mins);
              const formattedTotalDur = totalMins > 0 ? (totalMins >= 60 ? `${Math.floor(totalMins / 60)}j ${totalMins % 60}m` : `${totalMins} menit`) : null;

              const isFillingActive = fillStatus === "in_progress";
              const isPackingActive = packStatus === "in_progress";
              const isDualRunning = isFillingActive && isPackingActive;
              const showMultiStageBar = Boolean(mixPic || fillPic || packPic || sub.mixing || sub.filling || sub.packing || stageMixingTask || stageFillingTask || stagePackingTask);

              return (
                <div key={task.id} className={`border rounded-xl overflow-hidden transition-all ${isDualRunning ? 'border-amber-400 bg-amber-50/10' : selectedTasks.includes(task.id) ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : task.id)}
                  >
                    {selectedTasks.length > 0 && (
                      <div className="shrink-0" onClick={e => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedTasks.includes(task.id)} 
                          onCheckedChange={() => handleToggleTaskSelection(task.id)} 
                        />
                      </div>
                    )}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm bg-slate-100 text-slate-700 border border-slate-200">
                      {actOpt ? <actOpt.icon className="w-4 h-4 text-slate-700" /> : <ClipboardList className="w-4 h-4 text-slate-700" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{task.task_name}</p>
                        {isDualRunning && (
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[9px] px-1.5 py-0 h-4 font-semibold">
                            Paralel
                          </Badge>
                        )}
                        {formattedTotalDur && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-50 text-amber-900 border border-amber-200 shadow-2xs">
                            <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                            {formattedTotalDur}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${task.pic_name ? "bg-slate-100 text-slate-800 border border-slate-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
                          <User className="w-3 h-3 text-slate-500 shrink-0" /> {task.pic_name || "Belum ada PIC"}
                        </span>
                        {sub.filling?.pic_name && sub.packing?.pic_name && (
                          <span className="text-[10px] text-slate-500">
                            (Filling: {sub.filling.pic_name} · Kemas: {sub.packing.pic_name})
                          </span>
                        )}
                        {task.pendukung?.length > 0 && (
                          <span className="text-[10px] text-slate-500">+{task.pendukung.length} pendukung</span>
                        )}
                        {task.task_date && (
                          <span className="text-[10px] text-slate-400">· {format(new Date(task.task_date), "dd MMM", { locale: idLocale })}</span>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-medium shrink-0 ${statusCfg.cls}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusCfg.label}
                    </span>
                    
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleOpenEdit(task);
                      }} 
                      className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 transition-colors mr-1"
                      title="Edit Tugas / Ubah Status"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleToggleTaskSelection(task.id);
                      }} 
                      className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600 transition-colors mr-1"
                      title="Pilih untuk Dihapus"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                  </div>

                  {/* Multi-stage Sub Progress in 1 Card */}
                  {showMultiStageBar && (
                    <div className="px-3 pb-2.5 pt-1.5 border-t border-slate-100 bg-slate-50/50 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                      {/* 1. Mixing */}
                      <div className="p-2 rounded-lg bg-white border border-slate-200 flex flex-col justify-between space-y-1">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-semibold text-slate-800">1. Mixing</span>
                          {mixInfo.formatted && (
                            <span className="text-[9px] font-bold text-amber-900 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200 inline-flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5 text-amber-600" /> {mixInfo.formatted}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-600 flex items-center justify-between gap-1 flex-wrap">
                          <span className="font-medium truncate max-w-[100px]">{mixPic || "—"}</span>
                          {mixStatus === "completed" ? (
                            <span className="text-[9px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">✓ Selesai</span>
                          ) : mixStatus === "in_progress" ? (
                            <span className="text-[9px] font-bold text-blue-800 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200 animate-pulse">▶ Berjalan</span>
                          ) : (
                            <span className="text-[9px] text-slate-400">Belum</span>
                          )}
                        </div>
                        {mixInfo.range && (
                          <div className="text-[8.5px] text-slate-400 font-mono pt-0.5 border-t border-slate-100 flex items-center gap-1">
                            <span>🕒 {mixInfo.range}</span>
                          </div>
                        )}
                      </div>

                      {/* 2. Filling */}
                      <div className="p-2 rounded-lg bg-white border border-slate-200 flex flex-col justify-between space-y-1">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-semibold text-slate-800">2. Filling</span>
                          {fillInfo.formatted && (
                            <span className="text-[9px] font-bold text-amber-900 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200 inline-flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5 text-amber-600" /> {fillInfo.formatted}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-600 flex items-center justify-between gap-1 flex-wrap">
                          <span className="font-medium truncate max-w-[100px]">{fillPic || "—"}</span>
                          {fillStatus === "completed" ? (
                            <span className="text-[9px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">✓ Selesai</span>
                          ) : fillStatus === "in_progress" ? (
                            <span className="text-[9px] font-bold text-blue-800 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200 animate-pulse">▶ Berjalan</span>
                          ) : (
                            <span className="text-[9px] text-slate-400">Belum</span>
                          )}
                        </div>
                        {fillInfo.range && (
                          <div className="text-[8.5px] text-slate-400 font-mono pt-0.5 border-t border-slate-100 flex items-center gap-1">
                            <span>🕒 {fillInfo.range}</span>
                          </div>
                        )}
                      </div>

                      {/* 3. Pengemasan */}
                      <div className="p-2 rounded-lg bg-white border border-slate-200 flex flex-col justify-between space-y-1">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-semibold text-slate-800">3. Pengemasan</span>
                          {packInfo.formatted && (
                            <span className="text-[9px] font-bold text-amber-900 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200 inline-flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5 text-amber-600" /> {packInfo.formatted}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-600 flex items-center justify-between gap-1 flex-wrap">
                          <span className="font-medium truncate max-w-[100px]">{packPic || "—"}</span>
                          {packStatus === "completed" ? (
                            <span className="text-[9px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">✓ Selesai</span>
                          ) : packStatus === "in_progress" ? (
                            <span className="text-[9px] font-bold text-blue-800 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200 animate-pulse">▶ Berjalan</span>
                          ) : (
                            <span className="text-[9px] text-slate-400">Belum</span>
                          )}
                        </div>
                        {packInfo.range && (
                          <div className="text-[8.5px] text-slate-400 font-mono pt-0.5 border-t border-slate-100 flex items-center gap-1">
                            <span>🕒 {packInfo.range}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-slate-100 bg-slate-50/40 pt-3 space-y-2 text-xs text-slate-600">
                      {task.target_quantity > 0 && (
                        <div className="flex justify-between">
                          <span>Target:</span>
                          <span className="font-semibold text-slate-900">{task.target_quantity} {task.unit}</span>
                        </div>
                      )}
                      {task.overlap_info?.isOverlapping && (
                        <div className="flex justify-between text-slate-700 bg-slate-100 px-2 py-1 rounded border border-slate-200 font-medium">
                          <span>Waktu Overlap (Berjalan Bersamaan):</span>
                          <span className="font-bold">{task.overlap_info.overlapFormatted}</span>
                        </div>
                      )}
                      {task.pendukung?.length > 0 && (
                        <div>
                          <span className="font-medium text-slate-800 flex items-center gap-1 mb-1"><UserPlus className="w-3 h-3 text-slate-500" /> Tenaga Pendukung:</span>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {task.pendukung.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] bg-white text-slate-700 border-slate-300">{p.name} ({ROLE_LABELS[p.role] || p.role})</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {task.status === "completed" && task.completed_quantity > 0 && (
                        <div className="flex justify-between">
                          <span>Output:</span>
                          <span className="font-semibold text-emerald-700">{task.completed_quantity} {task.unit}</span>
                        </div>
                      )}
                      {task.status === "completed" && task.duration_minutes > 0 && (
                        <div className="flex justify-between">
                          <span>Durasi:</span>
                          <span className="font-semibold text-slate-800 flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-500" />
                            {task.duration_minutes >= 60
                              ? `${Math.floor(task.duration_minutes / 60)}j ${task.duration_minutes % 60}m`
                              : `${task.duration_minutes} menit`}
                          </span>
                        </div>
                      )}
                      {task.notes && (() => {
                        const cleanNotes = task.notes
                          .split(/\s*\|\s*|\n+/)
                          .filter(segment => !segment.toLowerCase().includes('takaran bahan'))
                          .join(' | ')
                          .replace(/\[Selesai Dikerjakan\]\s*Menunggu Validasi QC/gi, '')
                          .trim();
                        if (!cleanNotes) return null;
                        return (
                          <p className="italic text-slate-500 flex items-start gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                            <span>{cleanNotes}</span>
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Edit Task Modal */}
      <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Edit Tugas Produksi
            </DialogTitle>
            <DialogDescription>
              Ubah target kuantitas, status, jenis tugas, atau penanggung jawab (PIC).
            </DialogDescription>
          </DialogHeader>
          {editingTask && editForm && (
            <div className="space-y-3.5 py-2">
              {editingTask.status === "completed" && editForm.status !== "completed" && (
                <div className="bg-amber-50 text-amber-700 p-3 rounded-xl flex items-start gap-2 text-xs border border-amber-200">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <p>Anda akan mengubah status tugas dari <b>Selesai</b>. Stok bahan baku yang sudah terpakai akan dikembalikan secara otomatis.</p>
                </div>
              )}

              {/* Target Qty & Unit */}
              <div className="space-y-1.5 border border-primary/20 bg-primary/5 rounded-xl p-3">
                <Label className="text-xs font-semibold flex items-center justify-between text-slate-800">
                  <span>Target Pengerjaan / Qty *</span>
                  <span className="text-[11px] font-normal text-muted-foreground">Kuantitas target tugas ini</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="Contoh: 422"
                    value={editForm.target_quantity}
                    onChange={(e) => setEditForm(f => ({ ...f, target_quantity: e.target.value }))}
                    className="bg-white font-bold text-slate-900 h-9"
                  />
                  <Select 
                    value={editForm.unit} 
                    onValueChange={(u) => setEditForm(f => ({ ...f, unit: u }))}
                  >
                    <SelectTrigger className="w-[90px] h-9 bg-white shrink-0 font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">pcs</SelectItem>
                      <SelectItem value="liter">liter</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="gram">gram</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Status Tugas */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Status Tugas</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Jenis Tugas */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Jenis Tugas</Label>
                <Select value={editForm.activity_type} onValueChange={v => setEditForm(f => ({ ...f, activity_type: v, unit: v === 'racik' ? (f.unit === 'pcs' ? 'liter' : f.unit) : 'pcs' }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          {opt.icon && <opt.icon className="w-4 h-4" />} {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Penanggung Jawab (PIC) */}
              <div className="pt-1">
                <PicSelector
                  label="Penanggung Jawab (PIC)"
                  value={editForm.pic_name}
                  onChange={(name, member) => setEditForm(f => ({ 
                    ...f, 
                    pic_name: name,
                    pic_id: member?.id || "",
                    pic_role: member?.role || ""
                  }))}
                  picType="task"
                  taskType={editForm.activity_type}
                />
              </div>

              {/* Catatan Tugas */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Catatan Tugas (Opsional)</Label>
                <Textarea
                  placeholder="Tambahkan catatan khusus untuk pengerjaan tugas ini..."
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="text-xs min-h-[60px] resize-none"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditingTask(null)}>
              Batal
            </Button>
            <Button size="sm" onClick={handleSaveEdit} disabled={editTaskMutation.isPending}>
              {editTaskMutation.isPending ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
}
